import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Api } from '../api';
import { AppointmentSlotsParams } from '../schemas/getAppointmentSlots';

type UseAppointmentSlotsParams = {
  patientGuid?: string;
  weekOffset: number;
  clinSubtype?: string;
  clinSpeccat?: string;
  clinSpecarea?: string;
  apptTypeCode?: string;
  clinGuid?: string;
  enabled?: boolean;
};

export const useAppointmentSlots = ({
  patientGuid,
  weekOffset,
  clinSubtype,
  clinSpeccat,
  clinSpecarea,
  apptTypeCode,
  clinGuid,
  enabled = true
}: UseAppointmentSlotsParams) => {
  return useQuery({
    queryKey: [
      'appointmentSlots',
      patientGuid,
      weekOffset,
      clinSubtype,
      clinSpeccat,
      clinSpecarea,
      apptTypeCode,
      clinGuid
    ],
    queryFn: () => {
      const params: AppointmentSlotsParams = {
        week_offset: weekOffset,
        clin_subtype: clinSubtype ?? undefined,
        clin_speccat: clinSpeccat ?? undefined,
        clin_specarea: clinSpecarea ?? undefined,
        patient_guid: patientGuid ?? undefined,
        appt_type_code: apptTypeCode ?? undefined,
        clin_guid: clinGuid ?? undefined,
        start: '',
        end: ''
      };
      return Api.booking.appointmentSlots(params);
    },
    select: (data) => {
      if (!data?.appointment_slots) return data;

      const rawGroups = data.appointment_slots.map((group) => ({
        ...group,
        slots: group.slots ?? []
      }));

      const allSlots = rawGroups.flatMap((group) => group.slots);

      const slotsByLocalDate = new Map<string, typeof allSlots>();

      allSlots.forEach((slot) => {
        if (!slot?.slot_datetime) return;

        const localDateStr = dayjs(slot.slot_datetime).format('YYYY-MM-DD');

        if (!slotsByLocalDate.has(localDateStr)) {
          slotsByLocalDate.set(localDateStr, []);
        }

        slotsByLocalDate.get(localDateStr)!.push(slot);
      });

      const remappedGroups = rawGroups.map((group) => {
        const firstSlotTime = group.slots[0]?.slot_datetime;

        const groupLocalDateStr = firstSlotTime ? dayjs(firstSlotTime).format('YYYY-MM-DD') : null;

        const localSlots = groupLocalDateStr ? slotsByLocalDate.get(groupLocalDateStr) || [] : group.slots;

        const sortedSlots = [...localSlots].sort(
          (a, b) => dayjs(a.slot_datetime).valueOf() - dayjs(b.slot_datetime).valueOf()
        );

        return {
          ...group,
          slots: sortedSlots
        };
      });

      return {
        ...data,
        appointment_slots: remappedGroups
      };
    },
    enabled: enabled && !!patientGuid
  });
};
